import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  MonetizationOn,
  CalendarToday,
  Assessment,
  BarChart,
  Timeline,
} from "@mui/icons-material";
import {
  useDividendAnalysis,
  useDividendStats,
  useTopDividendHoldings,
  usePortfolioDividendStats,
} from "../Charts/hooks/useDividendAnalysis";
import YearNavigation from "./YearNavigation";
import DividendGraph from "./DividendGraph";
import {
  MetricCardSkeleton,
  HoldingsListSkeleton,
  PaymentHistorySkeleton,
  GraphSkeleton,
} from "./DividendSkeletons";
import DividendPaymentsTable from "./DividendPaymentsTable";
import DividendStatsTable from "./DividendStatsTable";
import TopDividendHoldingsTable from "./TopDividendHoldingsTable";
import dayjs from "dayjs";

const DividendsView: React.FC = () => {
  const currencySymbol = "$";
  
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [viewMode, setViewMode] = useState<"year" | "graph" | "stats">("year");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>("paymentDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [paymentsPerPage, setPaymentsPerPage] = useState<number>(25);

  // Stats view state
  const [statsPage, setStatsPage] = useState<number>(1);
  const [statsPerPage, setStatsPerPage] = useState<number>(10);

  // Top dividend holdings state
  const [topHoldingsPage, setTopHoldingsPage] = useState<number>(1);
  const [topHoldingsPerPage, setTopHoldingsPerPage] = useState<number>(10);

  const {
    data: dividendData,
    isLoading,
    isFetching,
    error,
  } = useDividendAnalysis(
    viewMode === "graph" ? "all" : selectedYear,
    viewMode === "graph" ? "graph" : "year", // Only use 'graph' or 'year' modes
    currentPage,
    paymentsPerPage,
    sortBy,
    sortOrder
  );

  // Stats hook (only used when viewMode is 'stats')
  const {
    data: statsData,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
    error: statsError,
  } = useDividendStats(statsPage, statsPerPage);

  // Top dividend holdings hook (only used when viewMode is 'stats')
  const {
    data: topHoldingsData,
    isLoading: isTopHoldingsLoading,
    isFetching: isTopHoldingsFetching,
    error: topHoldingsError,
  } = useTopDividendHoldings(topHoldingsPage, topHoldingsPerPage);

  // Portfolio dividend stats hook (only used when viewMode is 'stats')
  const {
    data: portfolioStatsData,
    isLoading: isPortfolioStatsLoading,
    isFetching: isPortfolioStatsFetching,
    error: portfolioStatsError,
  } = usePortfolioDividendStats();


  // Reset to page 1 when year, sorting, or per-page limit changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, viewMode, sortBy, sortOrder, paymentsPerPage]);

  // Reset stats page when switching to stats view
  React.useEffect(() => {
    setStatsPage(1);
  }, [viewMode, statsPerPage]);

  // Reset top holdings page when switching to stats view
  React.useEffect(() => {
    setTopHoldingsPage(1);
  }, [viewMode, topHoldingsPerPage]);

  // Handle table sorting
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Show error state
  if (error && !dividendData) {
    return (
      <Alert severity="error" sx={{ mx: 3 }}>
        Failed to load dividend data: {error.message}
      </Alert>
    );
  }

  // Show initial loading state only when no data is available
  if (isLoading && !dividendData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show info state only when no data after loading
  if (!isLoading && !dividendData) {
    return (
      <Alert severity="info" sx={{ mx: 3 }}>
        No dividend data available
      </Alert>
    );
  }

  const { summary } = dividendData || { summary: null };

  const MetricCard = ({
    title,
    value,
    subtitle,
    icon,
    color = "primary",
    trend,
  }: {
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ReactNode;
    color?: "primary" | "success" | "info" | "warning";
    trend?: number;
  }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
            : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.05)",
        borderRadius: 3,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          // REMOVED transform for performance: transform: "translateY(-2px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 8px 25px rgba(0,0,0,0.3)"
              : "0 8px 25px rgba(0,0,0,0.1)",
        },
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `${
                color === "primary"
                  ? "#667eea"
                  : color === "success"
                  ? "#22c55e"
                  : color === "info"
                  ? "#3b82f6"
                  : "#f59e0b"
              }15`,
              color:
                color === "primary"
                  ? "#667eea"
                  : color === "success"
                  ? "#22c55e"
                  : color === "info"
                  ? "#3b82f6"
                  : "#f59e0b",
            }}
          >
            {icon}
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={
                trend > 0 ? (
                  <TrendingUp sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16 }} />
                )
              }
              label={`${trend > 0 ? "+" : ""}${trend}%`}
              sx={{
                backgroundColor:
                  trend > 0
                    ? "rgba(34, 197, 94, 0.1)"
                    : "rgba(239, 68, 68, 0.1)",
                color: trend > 0 ? "#22c55e" : "#ef4444",
                border: "none",
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          {value}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* View Mode Toggle */}
      <Box display="flex" justifyContent="center" mb={3}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
          sx={{
            // Container styling for unified appearance
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 8px rgba(0,0,0,0.3)"
                : "0 2px 8px rgba(0,0,0,0.1)",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.12)",
            
            "& .MuiToggleButton-root": {
              // Remove individual button styling
              border: "none",
              borderRadius: 0,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.5,
              minWidth: 120,
              position: "relative",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              
              // Default inactive state
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.02)",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.7)"
                  : "rgba(0,0,0,0.6)",
              
              // Add vertical separators between buttons
              "&:not(:last-child)::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "20%",
                bottom: "20%",
                width: "1px",
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.1)",
                transition: "opacity 0.3s ease",
              },
              
              // Hide separator on selected and adjacent buttons
              "&.Mui-selected::after, &.Mui-selected + .MuiToggleButton-root::before": {
                opacity: 0,
              },
              
              // Hover state for inactive buttons
              "&:not(.Mui-selected):hover": {
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.9)"
                    : "rgba(0,0,0,0.8)",
                // REMOVED transform for performance: transform: "translateY(-1px)",
              },
              
              // Selected state
              "&.Mui-selected": {
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)"
                    : "linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 0.9) 100%)",
                color: "#ffffff",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
                zIndex: 1,
                
                "&:hover": {
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, rgba(34, 197, 94, 1) 0%, rgba(22, 163, 74, 0.9) 100%)"
                      : "linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 1) 100%)",
                  transform: "none",
                },
              },
            },
          }}
        >
          <ToggleButton value="year">
            <BarChart sx={{ fontSize: 18, mr: 1 }} />
            Year View
          </ToggleButton>
          <ToggleButton value="stats">
            <Assessment sx={{ fontSize: 18, mr: 1 }} />
            Stats View
          </ToggleButton>
          <ToggleButton value="graph">
            <Timeline sx={{ fontSize: 18, mr: 1 }} />
            Graph View
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Year Navigation (only shown in year view) */}
      {viewMode === "year" && (
        <Box display="flex" justifyContent="center" mb={4}>
          <YearNavigation
            currentYear={selectedYear}
            onYearChange={setSelectedYear}
            minYear={2020}
            maxYear={new Date().getFullYear()}
          />
        </Box>
      )}

      {/* Graph View */}
      {viewMode === "graph" && (
        <>
          {isFetching ? (
            <GraphSkeleton />
          ) : dividendData?.yearlyData ? (
            <DividendGraph yearlyData={dividendData.yearlyData} />
          ) : null}
        </>
      )}

      {/* Stats View Content */}
      {viewMode === "stats" && (
        <>
          {/* Summary Cards */}
          {isPortfolioStatsFetching ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 3,
                mb: 4,
              }}
            >
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 3,
                mb: 4,
              }}
            >
              <MetricCard
                title="Dividends"
                value={`${currencySymbol}${
                  portfolioStatsData?.totalDividendsAllTime?.toLocaleString() ?? "0"
                }`}
                subtitle="All time"
                icon={<MonetizationOn />}
                color="success"
              />

              <MetricCard
                title="Interest"
                value={`${currencySymbol}${
                  portfolioStatsData?.totalInterestAllTime?.toLocaleString() ?? "0"
                }`}
                subtitle="All time"
                icon={<CalendarToday />}
                color="info"
              />

              <MetricCard
                title="Portfolio Yield"
                value={`${
                  portfolioStatsData?.portfolioWeightedDividendYield?.toFixed(2) ?? "0.00"
                }%`}
                subtitle="Weighted dividend yield"
                icon={<Assessment />}
                color="warning"
              />

              <MetricCard
                title="Yield on Cost"
                value={`${
                  portfolioStatsData?.portfolioYieldOnCost?.toFixed(2) ?? "0.00"
                }%`}
                subtitle="Based on purchase price"
                icon={<TrendingUp />}
                color="primary"
              />
            </Box>
          )}

          {/* Top Dividend Holdings */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: "rgba(34, 197, 94, 0.15)",
                  color: "#22c55e",
                }}
              >
                <TrendingUp />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Top Dividend Holdings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your highest dividend-producing stocks with live yield data
                </Typography>
              </Box>
            </Box>

            {isTopHoldingsFetching ? (
              <HoldingsListSkeleton />
            ) : topHoldingsData ? (
              <TopDividendHoldingsTable
                holdings={topHoldingsData.holdings}
                pagination={topHoldingsData.pagination}
                onPageChange={setTopHoldingsPage}
                onLimitChange={setTopHoldingsPerPage}
                isLoading={isTopHoldingsFetching}
              />
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  No dividend-paying holdings found
                </Typography>
              </Box>
            )}
          </Box>

          {/* Cumulative Dividend Statistics */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: "rgba(59, 130, 246, 0.15)",
                  color: "#3b82f6",
                }}
              >
                <Assessment />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Cumulative Dividend Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total dividends received from each company over all time
                </Typography>
              </Box>
            </Box>

            {isStatsFetching ? (
              <PaymentHistorySkeleton />
            ) : statsData ? (
              <DividendStatsTable
                companies={statsData.companies}
                pagination={statsData.pagination}
                onPageChange={setStatsPage}
                onLimitChange={setStatsPerPage}
                isLoading={isStatsFetching}
              />
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  No dividend statistics available
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Year View Content */}
      {viewMode === "year" && (
        <>
          {/* Summary Cards */}
          {isFetching ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },
                  gap: 3,
                  maxWidth: 600,
                  width: "100%",
                }}
              >
                <MetricCardSkeleton />
                <MetricCardSkeleton />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },
                  gap: 3,
                  maxWidth: 600, // Half the typical container width
                  width: "100%",
                }}
              >
                <MetricCard
                  title="Dividends"
                  value={`${currencySymbol}${
                    summary?.totalDividendsCurrentYear?.toLocaleString() ?? "0"
                  }`}
                  subtitle={`Received in ${selectedYear}`}
                  icon={<MonetizationOn />}
                  color="success"
                />

                <MetricCard
                  title="Interest"
                  value={`${currencySymbol}${
                    summary?.totalInterestCurrentYear?.toLocaleString() ?? "0"
                  }`}
                  subtitle={`Cash interest in ${selectedYear}`}
                  icon={<CalendarToday />}
                  color="info"
                />
              </Box>
            </Box>
          )}

          {/* Dividend Payment History */}
          {isFetching ? (
            <PaymentHistorySkeleton />
          ) : (
            dividendData?.payments && (
              <Box sx={{ mb: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      background: "rgba(59, 130, 246, 0.15)",
                      color: "#3b82f6",
                    }}
                  >
                    <CalendarToday />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Recent Dividend Payments
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your dividend payment history
                    </Typography>
                  </Box>
                </Box>

                <DividendPaymentsTable
                  payments={dividendData.payments}
                  pagination={dividendData.pagination}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  onPageChange={setCurrentPage}
                  onLimitChange={setPaymentsPerPage}
                  isLoading={isFetching}
                />
              </Box>
            )
          )}
        </>
      )}

    </Box>
  );
};

export default DividendsView;
